import type { ServiceProviderFeaturesUpdatePayload } from 'types';

export const RETAINER_FLOW_PREFIX = 'RETAINER_FLOW_';

const FEATURE_DESCRIPTION_KEYS: Record<string, string> = {
    CLIENT_ENGAGEMENT: 'clientEngagementFeatureDescription',
    FINANCIALS: 'financialsFeatureDescription',
    PARTNER_ORGANIZATIONS: 'partnerOrganizationsFeatureDescription',
    REPLY_SUGGESTIONS: 'replySuggestionsFeatureDescription',
    RETAINER_TEMPLATES: 'retainerTemplatesFeatureDescription',
};

/** Locale key for a checkbox feature. Unknown features have no secondary line. */
export const featureDescriptionKey = (name: string): string | null =>
    FEATURE_DESCRIPTION_KEYS[name] ?? null;

const RETAINER_FLOW_DESCRIPTION_KEYS: Record<string, string> = {
    RETAINER_FLOW_FREE: 'retainerFlowFreeDescription',
    RETAINER_FLOW_LEGACY_SERVICED_OR_NON_SERVICED: 'retainerFlowLegacyDescription',
    RETAINER_FLOW_SERVICED_OR_NON_SERVICED: 'retainerFlowServicedDescription',
    RETAINER_FLOW_TWO_STAGE_SERVICE_THEN_CONSULTANT_SERVICED_AGREEMENT:
        'retainerFlowTwoStageDescription',
};

/** Locale key for the selected flow. `null` flow is the legacy default. */
export const retainerFlowDescriptionKey = (flow: string | null): string | null => {
    if (!flow) return 'noRetainerFlowDescription';
    return RETAINER_FLOW_DESCRIPTION_KEYS[flow] ?? null;
};

export const isRetainerFlowFeature = (name: string): boolean =>
    name.startsWith(RETAINER_FLOW_PREFIX);

export const splitAvailableFeatures = (
    available: string[]
): { features: string[]; retainerFlows: string[] } => ({
    features: available.filter((name) => !isRetainerFlowFeature(name)),
    retainerFlows: available.filter(isRetainerFlowFeature),
});

const sortedDifference = (source: string[], exclude: string[]): string[] => {
    const excluded = new Set(exclude);
    return Array.from(new Set(source.filter((name) => !excluded.has(name)))).sort();
};

export const computeFeatureChanges = (
    enabled: string[],
    selected: string[]
): ServiceProviderFeaturesUpdatePayload => ({
    add: sortedDifference(selected, enabled),
    remove: sortedDifference(enabled, selected),
});

export const formatFeatureName = (name: string): string => {
    const words = name.toLowerCase().split('_').join(' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
};

const RETAINER_FLOW_LABEL_PREFIX = 'Retainer flow ';

/** Feature label without the leading "Retainer flow" the column header already shows. */
export const formatRetainerFlowName = (name: string): string => {
    const label = formatFeatureName(name);
    if (!label.startsWith(RETAINER_FLOW_LABEL_PREFIX)) {
        return label;
    }
    const rest = label.slice(RETAINER_FLOW_LABEL_PREFIX.length);
    return rest.charAt(0).toUpperCase() + rest.slice(1);
};

export const selectedRetainerFlow = (selected: string[]): string | null =>
    selected.find(isRetainerFlowFeature) ?? null;
